// @flow

import { completeRequest, beginRequest } from "@store/utils";
import type { DepartmentSpecificScriptsState } from "./types";
import type { Action } from "redux-action";
import type { ApprovableContainer } from "@modules/service/types";
import type { DepartmentSpecificScript } from "@modules/service/reducer/department-specific-scripts/types";

export type ShowDepatmentSpecificScriptModalRequest = $Exact<{
  show: boolean,
  step?: number,
}>;

const startRequest = beginRequest(["requestsInProgress"]);

const finishRequest = completeRequest(["requestsInProgress"]);

export const onGetDepartmentSpecificScripts = (state: DepartmentSpecificScriptsState) => () =>
  startRequest(state);

export const onGetDepartmentSpecificScriptsSuccess = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  list: payload,
});

export const onGetDepartmentSpecificScriptsError = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onNewDepartmentSpecificScript = (state: DepartmentSpecificScriptsState) => (
  action: Action<void>,
) => ({
  ...state,
  draft: {
    scriptText: null,
    masterScript: null,
    isActive: null,
  },
});

export const onSetDepartmentSpecificScriptDraft = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  current: payload,
});

export const onNewDepartmentSpecificScriptAssignments = (state: DepartmentSpecificScriptsState) => (
  action: Action<void>,
) => ({
  ...state,
  assignments: [],
});

export const onShowAddDepartmentSpecificScriptModal = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Action<ShowDepatmentSpecificScriptModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showAddDepartmentSpecificScriptModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onUpdateDepartmentSpecificScriptDraft = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  draft: payload,
});

export const onGetDepartmentSpecificScriptActiveSuccess = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetDepartmentSpecificScriptActiveError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onAddDepartmentSpecificScript = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...startRequest(state),
});

export const onAddDepartmentSpecificScriptSuccess = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
});

export const onAddDepartmentSpecificScriptError = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onSetDepartmentSpecificScriptAssignments = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  assignments: payload,
});

export const onSetDepartmentSpecificScriptDraftAssignments = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  draftAssignments: payload,
});

export const onGetDepartmentSpecificScript = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...startRequest(state),
});

export const onGetDepartmentSpecificScriptSuccess = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetDepartmentSpecificScriptError = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowDepartmentSpecificScriptEditModal = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditModal: payload,
  },
});

export const onSetShowSubmitDepartmentSpecificScriptModal = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showSubmitModal: payload,
  },
});

export const onSetShowCancelDepartmentSpecificScriptModal = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelModal: payload,
  },
});

export const onSubmitDepartmentSpecificScriptForReviewError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelDepartmentSpecificScriptReviewError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onStartDepartmentSpecificScriptDraftSuccess = (
  state: DepartmentSpecificScriptsState,
) => (action: Action<ApprovableContainer<DepartmentSpecificScript>>) => ({
  ...state,
  draft: (action.payload && action.payload.active) || {},
});

export const onSetShowDiscardDepartmentSpecificScriptModal = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDiscardModal: payload,
  },
});

export const onSetShowCancelDiscardDepartmentSpecificScriptModal = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelDiscardModal: payload,
  },
});

export const onDuplicateDepartmentSpecificScriptSuccess = (
  state: DepartmentSpecificScriptsState,
) => (action: Action<DepartmentSpecificScript>) => ({
  ...state,
  draft: {
    masterScript: (action.payload || {}).masterScript,
    scriptText: (action.payload || {}).scriptText,
  },
});

export const onSetShowDisableDepartmentSpecificScriptModal = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDisableModal: payload,
  },
});

export const onStartDepartmentSpecificScriptDraftError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDiscardDepartmentSpecificScriptDraftError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDuplicateDepartmentSpecificScriptError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelDepartmentSpecificScriptReviewAndDiscardError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDisableDepartmentSpecificScriptError = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onUpdateDepartmentSpecificScriptAssignmentsError = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowEditDepartmentSpecificScriptAssignmentsModal = (
  state: DepartmentSpecificScriptsState,
) => ({ payload }: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditAssignmentsModal: payload,
  },
});

export const onUpdateDepartmentSpecificScript = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...startRequest(state),
  modals: {
    ...state.modals,
    showEditModal: false,
  },
});

export const onUpdateDepartmentSpecificScriptSuccess = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
});

export const onUpdateDepartmentSpecificScriptError = (state: DepartmentSpecificScriptsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});
